import { ReportingModel } from '@pukka/api';
import { assertUtil } from '@pukka/common/utils';
import { endOfDay, startOfDay, differenceInDays, addDays, format, startOfMonth, isValid } from 'date-fns';
import { DeepWritable } from 'ts-essentials';

interface Cache {
  grossSales: number;
  netSales: number;
  trx: number;
  grossProfit: number;
  topItems: {
    name: string;
    item_sold: number;
    gross_sales: number;
    net_sales: number;
    gross_profit: number;
  }[];
}

interface Item {
  name: string;
  grossSales: number;
  netSales: number;
  grossProfit: number;
}

const cache: Map<number, Map<string, Cache>> = new Map();

const items = [
  {
    name: 'Es Teh Manis',
    grossSales: 7_000,
    netSales: 6_000,
    grossProfit: 5_500,
  },
  {
    name: 'Nasi Goreng',
    grossSales: 20_000,
    netSales: 13_000,
    grossProfit: 10_500,
  },
  {
    name: 'Banana Split',
    grossSales: 35_000,
    netSales: 27_000,
    grossProfit: 20_500,
  },
  {
    name: 'Djarum Super',
    grossSales: 33_900,
    netSales: 31_900,
    grossProfit: 1_900,
  },
  {
    name: 'Lifebuoy',
    grossSales: 24_900,
    netSales: 23_900,
    grossProfit: 2_900,
  },
] satisfies Item[];
const itemMap: Map<string, Item> = items.reduce(
  (sum, item) => {
    return sum.set(item.name, item);
  },
  new Map() as Map<string, Item>,
);
const itemLength = items.length;

function combineMultipleOutletReportingData(
  datas: DeepWritable<ReportingModel.SalesSummaryResponseData & ReportingModel.TopItemsResponseData>[],
): ReportingModel.SalesSummaryResponse & ReportingModel.TopItemsResponse {
  const res = datas.reduce(
    (sum, data) => {
      data.topItems.forEach((item) => {
        const _item = sum.topItems.find((_i) => _i.name === item.name);

        if (_item) {
          _item.gross_sales += item.gross_sales;
          _item.net_sales += item.net_sales;
          _item.gross_profit += item.gross_profit;
          _item.item_sold += item.item_sold;
        } else {
          sum.topItems.push(item);
        }
      });

      return {
        salesSummaryReport: {
          grossSales: sum.salesSummaryReport.grossSales + data.salesSummaryReport.grossSales,
          netSales: sum.salesSummaryReport.netSales + data.salesSummaryReport.netSales,
          numberOfTransactions: sum.salesSummaryReport.numberOfTransactions + data.salesSummaryReport.numberOfTransactions,
        },
        grossProfitReport: {
          grossProfit: sum.grossProfitReport.grossProfit + data.grossProfitReport.grossProfit,
        },
        topItems: sum.topItems.sort((a, b) => b.item_sold - a.item_sold),
      };
    },
    {
      salesSummaryReport: {
        grossSales: 0,
        netSales: 0,
        numberOfTransactions: 0,
      },
      grossProfitReport: {
        grossProfit: 0,
      },
      topItems: [] as Cache['topItems'],
    },
  );

  return {
    data: res,
  };
}

function generateReportingData(
  outletId: number,
  startDate?: Date,
  endDate?: Date,
): ReportingModel.SalesSummaryResponseData & ReportingModel.TopItemsResponseData {
  if (!cache.has(outletId)) {
    cache.set(outletId, new Map());
  }

  const outletCache = cache.get(outletId);

  assertUtil.assertIsNotUndefined(outletCache);

  const _startDate = isValid(startDate) ? startDate : startOfMonth(new Date());
  const _endDate = isValid(endDate) ? endDate : new Date();

  assertUtil.assertIsDate(_startDate);
  assertUtil.assertIsDate(_endDate);

  const start = startOfDay(_startDate);
  const end = endOfDay(_endDate);
  const diff = differenceInDays(end, start);

  const response = Array(diff)
    .fill(undefined)
    .map((u, i) => {
      const key = format(addDays(start, i), 'yyyy-mm-dd');

      if (!outletCache.has(key)) {
        // generate new data
        const soldItems = Array(Math.round(Math.random() * 10))
          .fill(undefined)
          .map(() => {
            return items[Math.floor(Math.random() * itemLength)];
          });
        const sum = {
          ...soldItems.reduce(
            (s, item) => {
              return {
                grossSales: s.grossSales + item.grossSales,
                netSales: s.netSales + item.netSales,
                grossProfit: s.grossProfit + item.grossProfit,
                topItems: s.topItems.set(item.name, {
                  key: item.name,
                  count: (s.topItems.get(item.name)?.count ?? 0) + 1,
                }),
              };
            },
            {
              grossSales: 0,
              netSales: 0,
              grossProfit: 0,
              topItems: new Map() as Map<
                string,
                {
                  key: string;
                  count: number;
                }
              >,
            },
          ),
          transactions: soldItems.length,
        };
        const topItems = [...sum.topItems.values()].sort((a, b) => b.count - a.count);

        outletCache.set(key, {
          grossSales: sum.grossSales,
          netSales: sum.netSales,
          grossProfit: sum.grossProfit,
          trx: sum.transactions,
          topItems: topItems.map(({ key: _key, count }) => {
            const item = itemMap.get(_key);

            assertUtil.assertIsNotUndefined(item);

            return {
              name: _key,
              item_sold: count,
              gross_sales: item.grossSales,
              net_sales: item.netSales,
              gross_profit: item.grossProfit,
            };
          }),
        });
      }

      return outletCache.get(key) as Cache;
    })
    .reduce(
      (sum, daily) => {
        daily.topItems.forEach((item) => {
          const _item = sum.topItems.find((_i) => _i.name === item.name);

          if (_item) {
            _item.item_sold += item.item_sold;
          } else {
            sum.topItems.push(item);
          }
        });

        return {
          salesSummaryReport: {
            grossSales: sum.salesSummaryReport.grossSales + daily.grossSales,
            netSales: sum.salesSummaryReport.netSales + daily.netSales,
            numberOfTransactions: sum.salesSummaryReport.numberOfTransactions + daily.trx,
          },
          grossProfitReport: {
            grossProfit: sum.grossProfitReport.grossProfit + daily.grossProfit,
          },
          topItems: sum.topItems.sort((a, b) => b.item_sold - a.item_sold),
        };
      },
      {
        salesSummaryReport: {
          grossSales: 0,
          netSales: 0,
          numberOfTransactions: 0,
        },
        grossProfitReport: {
          grossProfit: 0,
        },
        topItems: [] as Cache['topItems'],
      },
    );

  return {
    ...response,
    topItems: response.topItems
      .map((item) => {
        return {
          ...item,
          gross_profit: item.gross_profit * item.item_sold,
          gross_sales: item.gross_sales * item.item_sold,
          net_sales: item.net_sales * item.item_sold,
        };
      })
      .slice(0, 3),
  } satisfies ReportingModel.SalesSummaryResponseData & ReportingModel.TopItemsResponseData;
}

const mockTransactionSummaryReport = {
  transactionSummaryReport: {
    netSales: 123_456_789,
    totalCollected: 123,
    numberOfTransactions: 321_000,
  },
} satisfies ReportingModel.TransactionSummaryReportResponse['data'];

export { generateReportingData, combineMultipleOutletReportingData, mockTransactionSummaryReport };
